import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Mail, Phone, MapPin, ChevronRight, Loader2 } from "lucide-react";
import { PostAxios } from "../../helpers/axiosHelpers";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { useSiteInfo } from "../../hooks/useSiteInfo";

const ContactSection = ({ sectionData }) => {
  const { info } = useSiteInfo();
  const { t, language } = useLanguage();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? (key ? t(key) : ""));

  const InquirySchema = Yup.object().shape({
    name: Yup.string().min(2, "Name too short").required("Full name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    company: Yup.string().required("Company/Organization is required"),
    subject: Yup.string().required("Please provide a subject"),
    message: Yup.string()
      .min(10, "Message is too short")
      .max(1000, "Message must be 1000 characters or less")
      .required("Message is required"),
  });

  const formik = useFormik({
    initialValues: { name: "", email: "", company: "", subject: "", message: "" },
    validationSchema: InquirySchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await PostAxios("/inquiry", values);
        if (!response.success) throw new Error(response.msg || "Submission failed");
        resetForm();
        toast.success(response.msg || "Inquiry submitted successfully!");
      } catch (error) {
        toast.error("Submission failed. Please try again.");
      }
    },
  });

  const ErrorDisplay = ({ field }) =>
    formik.touched[field] && formik.errors[field] ? (
      <div className="text-red-500 text-[10px] font-bold mt-1 ml-1 uppercase">
        {formik.errors[field]}
      </div>
    ) : null;

  const businessHours = info("business_hours", t("contact.businessHours.value"));
  const responseTime = info("response_time", t("contact.responseTime.value"));

  return (
    <section className="py-16 bg-white" id="contact-us">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 bg-subidha-blue rounded-[2rem] p-10 text-white flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent-green/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 space-y-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-1 h-3 bg-accent-green rounded-full"></div>
                  <span className="text-[10px] font-black text-accent-green uppercase tracking-[0.25em]">
                    {pick(sectionData?.labelEn, sectionData?.labelJa, "contact.sectionLabel")}
                  </span>
                </div>
                <h3 className="text-3xl font-serif-brand font-black tracking-tight leading-tight">
                  {pick(sectionData?.titleEn, sectionData?.titleJa, "contact.title")}
                </h3>
                {sectionData?.subtitleEn && (
                  <p className="text-slate-300 text-sm mt-2">
                    {pick(sectionData?.subtitleEn, sectionData?.subtitleJa, "")}
                  </p>
                )}
              </div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-accent-green shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-1">
                      {t("contact.corporateInquiries")}
                    </p>
                    <p className="text-base font-bold">
                      {info("company_email", "solutions@subidha.jp")}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-accent-green shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-1">
                      {t("contact.officeLine")}
                    </p>
                    <p className="text-base font-bold">
                      {info("company_phone", "+81 (03) 9876 5432")}
                    </p>
                  </div>
                </div>
                {info("company_address") && (
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-accent-green shrink-0">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-1">
                        {t("contact.officeAddress")}
                      </p>
                      <p className="text-base font-bold">
                        {info("company_address")}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Business hours + response time */}
              <div className="border-t border-white/10 pt-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                    {t("contact.businessHours.label")}
                  </span>
                  <span className="text-xs font-bold text-white">{businessHours}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                    {t("contact.responseTime.label")}
                  </span>
                  <span className="text-xs font-bold text-accent-green">{responseTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3 bg-slate-50 rounded-[2rem] p-8 lg:p-12 border border-slate-100">
            <form onSubmit={formik.handleSubmit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("contact.fullName")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("name")}
                    type="text"
                    className={`w-full px-6 py-4 bg-white border ${formik.touched.name && formik.errors.name ? "border-red-500" : "border-slate-200"} rounded-2xl focus:ring-4 focus:ring-lime-500/20 outline-none transition-all`}
                    placeholder="Enter name"
                  />
                  <ErrorDisplay field="name" />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                    {t("contact.emailAddress")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("email")}
                    type="email"
                    className={`w-full px-6 py-4 bg-white border ${formik.touched.email && formik.errors.email ? "border-red-500" : "border-slate-200"} rounded-2xl focus:ring-4 focus:ring-lime-500/20 outline-none transition-all`}
                    placeholder="email@company.com"
                  />
                  <ErrorDisplay field="email" />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  {t("contact.company")} <span className="text-red-500">*</span>
                </label>
                <input
                  {...formik.getFieldProps("company")}
                  type="text"
                  className="w-full px-6 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-lime-500/20 outline-none transition-all"
                  placeholder="Enter company/organization"
                />
                <ErrorDisplay field="company" />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  {t("contact.subject")} <span className="text-red-500">*</span>
                </label>
                <input
                  {...formik.getFieldProps("subject")}
                  type="text"
                  className="w-full px-6 py-4 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-lime-500/20 outline-none transition-all"
                  placeholder="Enter subject of your inquiry"
                />
                <ErrorDisplay field="subject" />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  {t("contact.message")} <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...formik.getFieldProps("message")}
                  rows={6}
                  maxLength={1000}
                  className={`w-full px-6 py-4 bg-white border ${formik.touched.message && formik.errors.message ? "border-red-500" : "border-slate-200"} rounded-2xl focus:ring-4 focus:ring-lime-500/20 outline-none transition-all resize-y max-h-48 overflow-y-auto`}
                  placeholder="Describe your inquiry..."
                />
                <div className="flex items-center justify-between text-[10px] text-slate-400 px-1">
                  <span>Keep your message concise and clear.</span>
                  <span>{formik.values.message.length}/1000</span>
                </div>
                <ErrorDisplay field="message" />
              </div>

              <button
                type="submit"
                disabled={formik.isSubmitting}
                className="bg-lime-500 hover:bg-lime-600 disabled:bg-slate-300 text-white px-12 py-5 rounded-full font-black flex items-center space-x-3 transition-all shadow-xl shadow-lime-200 transform active:scale-95"
              >
                <span>
                  {formik.isSubmitting ? t("contact.sending") : t("contact.sendButton")}
                </span>
                {formik.isSubmitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
